import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { customFieldsApi, CustomFieldDefinition } from '../api/customFields';
import PremiumLayout from '../components/PremiumLayout';
import { PlusIcon, PencilIcon, TrashIcon, Cog6ToothIcon } from '@heroicons/react/24/outline';

const CustomFields: React.FC = () => {
  const [showModal, setShowModal] = useState(false);
  const [editingField, setEditingField] = useState<CustomFieldDefinition | null>(null);
  const [selectedEntityType, setSelectedEntityType] = useState('lead');
  const [formData, setFormData] = useState({
    entity_type: 'lead',
    field_name: '',
    field_type: 'text',
    label: '',
    is_required: false,
    default_value: '',
    options: '',
  });

  const queryClient = useQueryClient();

  const { data: fields = [], isLoading } = useQuery({
    queryKey: ['custom-fields', selectedEntityType],
    queryFn: () => customFieldsApi.getDefinitions(selectedEntityType),
  });

  const createMutation = useMutation({
    mutationFn: customFieldsApi.createDefinition,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['custom-fields'] });
      setShowModal(false);
      resetForm();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<CustomFieldDefinition> }) =>
      customFieldsApi.updateDefinition(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['custom-fields'] });
      setShowModal(false);
      setEditingField(null);
      resetForm();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: customFieldsApi.deleteDefinition,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['custom-fields'] });
    },
  });

  const resetForm = () => {
    setFormData({
      entity_type: selectedEntityType,
      field_name: '',
      field_type: 'text',
      label: '',
      is_required: false,
      default_value: '',
      options: '',
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const fieldData: any = {
      ...formData,
      options: formData.options ? formData.options.split(',').map(o => o.trim()) : undefined,
    };
    delete fieldData.options;

    if (editingField) {
      updateMutation.mutate({ id: editingField.id, data: fieldData });
    } else {
      createMutation.mutate(fieldData);
    }
  };

  const handleEdit = (field: CustomFieldDefinition) => {
    setEditingField(field);
    setFormData({
      entity_type: field.entity_type,
      field_name: field.field_name,
      field_type: field.field_type,
      label: field.label,
      is_required: field.is_required,
      default_value: field.default_value || '',
      options: field.options ? field.options.join(', ') : '',
    });
    setShowModal(true);
  };

  const handleDelete = (id: number) => {
    if (window.confirm('Are you sure you want to delete this custom field?')) {
      deleteMutation.mutate(id);
    }
  };

  if (isLoading) {
    return (
      <PremiumLayout title="Custom Fields">
        <div className="flex justify-center items-center h-96">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-indigo-200 border-t-indigo-600"></div>
        </div>
      </PremiumLayout>
    );
  }

  return (
    <PremiumLayout
      title="Custom Fields"
      subtitle="Define custom fields for your entities"
    >
      <div className="space-y-6">
        {/* Entity Type Selector */}
        <div className="premium-card p-4">
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Entity Type
          </label>
          <select
            value={selectedEntityType}
            onChange={(e) => {
              setSelectedEntityType(e.target.value);
              setFormData({ ...formData, entity_type: e.target.value });
            }}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg"
          >
            <option value="lead">Lead</option>
            <option value="account">Account</option>
            <option value="contact">Contact</option>
            <option value="activity">Activity</option>
            <option value="task">Task</option>
          </select>
        </div>

        {/* Actions */}
        <div className="flex justify-end">
          <button
            onClick={() => {
              resetForm();
              setShowModal(true);
            }}
            className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 flex items-center"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Create Custom Field
          </button>
        </div>

        {/* Fields List */}
        <div className="premium-card p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
            <Cog6ToothIcon className="h-6 w-6 mr-2" />
            Custom Fields ({fields.length})
          </h2>
          {fields.length === 0 ? (
            <div className="text-center py-12">
              <Cog6ToothIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No custom fields defined</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Label</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Field Name</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Required</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {fields.map((field) => (
                    <tr key={field.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {field.label}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {field.field_name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 capitalize">
                        {field.field_type}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {field.is_required ? (
                          <span className="px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                            Required
                          </span>
                        ) : (
                          <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">
                            Optional
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                        <button
                          onClick={() => handleEdit(field)}
                          className="text-indigo-600 hover:text-indigo-900"
                        >
                          <PencilIcon className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleDelete(field.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Create/Edit Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-start justify-center z-50 pt-4 pb-4 px-4">
            <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                {editingField ? 'Edit Custom Field' : 'Create Custom Field'}
              </h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Label
                  </label>
                  <input
                    type="text"
                    value={formData.label}
                    onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    required
                    placeholder="Display Name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Field Name (internal)
                  </label>
                  <input
                    type="text"
                    value={formData.field_name}
                    onChange={(e) => setFormData({ ...formData, field_name: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    required
                    placeholder="field_name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Field Type
                  </label>
                  <select
                    value={formData.field_type}
                    onChange={(e) => setFormData({ ...formData, field_type: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="text">Text</option>
                    <option value="number">Number</option>
                    <option value="date">Date</option>
                    <option value="boolean">Boolean</option>
                    <option value="select">Select</option>
                    <option value="textarea">Textarea</option>
                  </select>
                </div>
                {formData.field_type === 'select' && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Options (comma-separated)
                    </label>
                    <input
                      type="text"
                      value={formData.options}
                      onChange={(e) => setFormData({ ...formData, options: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                      placeholder="Option 1, Option 2, Option 3"
                    />
                  </div>
                )}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Default Value
                  </label>
                  <input
                    type="text"
                    value={formData.default_value}
                    onChange={(e) => setFormData({ ...formData, default_value: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.is_required}
                      onChange={(e) => setFormData({ ...formData, is_required: e.target.checked })}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-700">Required field</span>
                  </label>
                </div>
                <div className="flex space-x-4">
                  <button
                    type="submit"
                    className="flex-1 bg-indigo-600 text-white py-2 px-4 rounded-lg hover:bg-indigo-700"
                  >
                    {editingField ? 'Update' : 'Create'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      setEditingField(null);
                      resetForm();
                    }}
                    className="flex-1 bg-gray-200 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </PremiumLayout>
  );
};

export default CustomFields;
